// What a file chip says about one attachment, from its live state. Pure, so the composer
// chip and the chip in a sent message say exactly the same thing.
import { formatFileSize } from "@/utils/fileHelpers.js";

// Statuses the server is still working towards; readable and failed files are settled.
export const IN_FLIGHT = new Set(["pending", "processing"]);
const READABLE = new Set(["completed", "partial"]);

const pagesPhrase = (pages) =>
  pages.length === 1 ? `page ${pages[0]}` : `pages ${pages.join(", ")}`;

const sentence = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

/**
 * @param {Object} entry - { status, stage, progress, pages_total, pages_done,
 *   partial_pages, error, size }
 * @returns {{ tone: string, label: string, progress: number|null, tooltip: string|null }}
 *   tone: busy | ok | part | bad
 */
export function chipState(entry) {
  const status = entry.status;
  if (status === "uploading") {
    const fraction = Math.max(0, Math.min(1, entry.progress || 0));
    return {
      tone: "busy",
      label: `Uploading · ${Math.round(fraction * 100)}%`,
      progress: fraction,
      tooltip: null,
    };
  }
  if (IN_FLIGHT.has(status)) {
    if (entry.stage === "uploading") {
      return {
        tone: "busy",
        label: "Uploading…",
        progress: null,
        tooltip: null,
      };
    }
    const total = entry.pages_total || 0;
    if (entry.stage === "ocr" && total > 1) {
      const done = Math.min(entry.pages_done || 0, total);
      return {
        tone: "busy",
        label: `Reading page ${Math.min(done + 1, total)} of ${total}`,
        progress: done / total,
        tooltip: null,
      };
    }
    return { tone: "busy", label: "Reading…", progress: null, tooltip: null };
  }
  // Completed, or no status at all (a file from before statuses were kept): ready.
  if (status === "completed" || !status) {
    return {
      tone: "ok",
      label: entry.size ? formatFileSize(entry.size) : "Ready",
      progress: null,
      tooltip: null,
    };
  }
  if (status === "partial") {
    const pages = entry.partial_pages || [];
    return {
      tone: "part",
      label: pages.length
        ? `Read partly · ${pagesPhrase(pages)} unclear`
        : "Read partly",
      progress: null,
      tooltip: `Most of this file was read. ${
        sentence(entry.error) || "Some of it could not be read reliably."
      } Oliver will say so if it matters to your question.`,
    };
  }
  if (status === "failed") {
    const reason = sentence(entry.error) || "Couldn't read this file.";
    return { tone: "bad", label: reason, progress: null, tooltip: reason };
  }
  return { tone: "busy", label: "Reading…", progress: null, tooltip: null };
}

/** Whether the file is ready for Oliver (read, wholly or partly). */
export const isReadable = (entry) => READABLE.has(entry.status);

/** One sentence for screen readers when a file settles, or null while it is in flight. */
export function announcement(entry) {
  const name = entry.original_file_name || "File";
  if (entry.status === "completed") return `${name} ready`;
  if (entry.status === "partial") return `${name} partly read`;
  if (entry.status === "failed") return `${name} could not be read`;
  return null;
}
